import moment from "moment-timezone"

/**
 * Returns booking HTML template
 * @param {Object} booking - booking object
 * @param {Function} getStatusColor - function to resolve status color
 * @returns {string} HTML string
 */
export const getAgendaBookingHTML = (booking, getStatusColor) => {
  if (!booking) return ""

  const statusColor = getStatusColor(
    booking.bookingStatus,
    booking.locationType,
    booking.isAppbooking,
  )

  const bookingTime = moment
    .tz(booking.bookingSlot, "HH:mm", "America/Vancouver")
    .format("hh:mm A")

  return `
<div 
class="agenda-booking-card"
  data-booking-id="${booking._id}"
style="
  display:flex;
  gap:10%;
  padding:10px 12px;
  border-left:10px solid ${statusColor};
  background:${statusColor};
  border-radius:6px;
  margin-right:10px;
  box-shadow:0 1px 3px rgba(0,0,0,0.5);
  font-family:Arial,sans-serif;
  cursor:pointer;
"
>

  <!-- Time -->
  <div style="
    min-width:65px;
    text-align:center;
    font-size:17px;
    color:#fff;
    font-weight:700;
  ">
    ${bookingTime}
  </div>

  <!-- Content -->
  <div style="flex:1; display:flex; justify-content:space-between; gap:10px; color:#fff;">

    <!-- Left -->
    <div style="display:flex; flex-direction:column;">

      <div style="font-weight:bold; font-size:14px;">
        #${booking.shortBookingId} - ${booking.isAppbooking ? "App" : "Web"}
      </div>

      <div style="font-size:14px;">
        ${
          booking.booking
            ?.map(
              (serviceItem) => `
          <div style="margin-bottom:4px;">
            <div style="display:flex; gap:6px; font-weight:600;">
              <div>${serviceItem.service.serviceOption}</div>
              <div>•</div>
              <div>${serviceItem.service.packageName}</div>
            </div>

            ${
              serviceItem.addonService?.length
                ? `<div style="font-size:12px;">
                    Addons: ${serviceItem.addonService
                      .map((a) => a.addonServiceName)
                      .join(", ")}
                  </div>`
                : `<div style="font-size:12px;">No Addons</div>`
            }
          </div>
        `,
            )
            .join("") ?? ""
        }
      </div>

      <div style="font-size:14px; font-weight:600;">
        ${booking.firstName} ${booking.lastName} - +1 ${booking.phoneNumber}
      </div>
    </div>

    <!-- Right -->
    <div style="display:flex; flex-direction:column; text-align:left; width:40%;">

      <div style="font-weight:bold; font-size:14px;">
        ${booking.locationType === "shop" ? "Shop" : "Mobile"}
      </div>

      <div style="
        font-size:15px;
        font-style:italic;
        color:#fff;
        font-weight:700;
        text-transform:capitalize;
      ">
        ${booking.bookingStatus}
      </div>

      <div style="font-size:16px; font-weight:800;">
        $${booking.totalAmount}
        <span style="font-size:14px; font-weight:600;">
          ${booking.coupon?.couponCode ? `(${booking.coupon.couponCode})` : ""}
        </span>
      </div>

      ${
        booking.note
          ? `<div style="font-size:14px; font-weight:800;">
              ${booking.note}
            </div>`
          : ""
      }

    </div>
  </div>
</div>
`
}

export const getAgendaBookingHTMLShop = (booking, getStatusColor) => {
  if (!booking) return ""

  const statusColor = getStatusColor(
    booking.bookingStatus,
    booking.locationType,
    booking.isAppbooking,
  )

  const bookingTime = moment
    .tz(booking.bookingSlot, "HH:mm", "America/Vancouver")
    .format("hh:mm A")

  return `<div
  class="agenda-booking-card"
  data-booking-id="${booking._id}"
  style="
    --tv-scale: 1;
    display:flex;
    gap:3vw;
    padding:2vw 2.5vw;
    border-left:1.2vw solid ${statusColor};
    background:${statusColor};
    border-radius:1vw;
    margin:1vw;
    box-shadow:0 6px 18px rgba(0,0,0,0.6);
    font-family:Arial,sans-serif;
    cursor:pointer;
    width:95%;
  "
>

  <!-- Time -->
  <div style="
    min-width:10vw;
    text-align:center;
    font-size:calc(clamp(26px, 2.2vw, 70px) * var(--tv-scale));
    color:#fff;
    font-weight:800;
    letter-spacing:1px;
  ">
    ${bookingTime}
  </div>

  <!-- Content -->
  <div style="
    flex:1;
    display:flex;
    justify-content:space-between;
    gap:3vw;
    color:#fff;
  ">

    <!-- Left -->
    <div style="display:flex; flex-direction:column; gap:1vw;">

      <div style="
        font-weight:800;
        font-size:calc(clamp(20px, 1.6vw, 60px) * var(--tv-scale));
      ">
        #${booking.shortBookingId} • ${booking.isAppbooking ? "App" : "Web"}
      </div>

      <div style="
        font-size:calc(clamp(18px, 1.4vw, 54px) * var(--tv-scale));
      ">

        ${
          booking.booking?.map(
            (serviceItem) => `
            <div style="margin-bottom:1vw;">
              <div style="
                display:flex;
                gap:1vw;
                font-weight:700;
              ">
                <div>${serviceItem.service.serviceOption}</div>
                <div>•</div>
                <div>${serviceItem.service.packageName}</div>
              </div>

              ${
                serviceItem.addonService?.length
                  ? `<div style="
                      font-size:calc(clamp(16px, 1.2vw, 54px) * var(--tv-scale));
                      opacity:0.95;
                    ">
                      Addons: ${serviceItem.addonService
                        .map((a) => a.addonServiceName)
                        .join(", ")}
                    </div>`
                  : `<div style="
                      font-size:calc(clamp(16px, 1.2vw, 50px) * var(--tv-scale));
                      opacity:0.85;
                    ">
                      No Addons
                    </div>`
              }
            </div>
          `
          ).join("") ?? ""
        }

      </div>

      <div style="
        font-size:calc(clamp(18px, 1.5vw, 50px) * var(--tv-scale));
        font-weight:700;
      ">
        ${booking.firstName} ${booking.lastName} • +1 ${booking.phoneNumber}
      </div>
    </div>

    <!-- Right -->
    <div style="
      display:flex;
      flex-direction:column;
      gap:1vw;
      text-align:left;
      min-width:22vw;
    ">

      <div style="
        font-weight:800;
        font-size:calc(clamp(18px, 1.4vw, 60px) * var(--tv-scale));
      ">
        ${booking.locationType === "shop" ? "Shop" : "Mobile"}
      </div>

      <div style="
        font-size:calc(clamp(20px, 1.6vw, 60px) * var(--tv-scale));
        font-weight:900;
        text-transform:capitalize;
      ">
        ${booking.bookingStatus}
      </div>

      <div style="
        font-size:calc(clamp(24px, 2vw, 52px) * var(--tv-scale));
        font-weight:900;
      ">
        $${booking.totalAmount}
        <span style="
          font-size:calc(clamp(16px, 1.2vw, 50px) * var(--tv-scale));
          font-weight:700;
        ">
          ${booking.coupon?.couponCode ? `(${booking.coupon.couponCode})` : ""}
        </span>
      </div>

      ${
        booking.note
          ? `<div style="
              font-size:calc(clamp(18px, 1.4vw, 50px) * var(--tv-scale));
              font-weight:700;
              opacity:0.95;
            ">
              ${booking.note}
            </div>`
          : ""
      }

    </div>
  </div>
</div>
`
}

export const getDaysBookingHTML = (booking, getStatusColor) => {
  if (!booking) return ""

  const statusColor = getStatusColor(
    booking.bookingStatus,
    booking.locationType,
    booking.isAppbooking,
  )

  const bookingTime = moment
    .tz(booking.bookingSlot, "HH:mm", "America/Vancouver")
    .format("hh:mm A")

  return `
<div style="
  display:flex;
  padding:4px 0px;
  gap:2%;
  border-left:10px solid ${statusColor};
  background:${statusColor};
  border-radius:6px;
  box-shadow:0 1px 3px rgba(0,0,0,0.5);
  font-family:Arial,sans-serif;
">

<div style="display:flex; flex-direction:column;gap:0px;">
        <!-- Time -->
        <div style="
            min-width:65px;
            text-align:center;
            font-size:15px;
            color:#fff;
            font-weight:700;
        ">
            ${bookingTime}
        </div>
      <div style="
        font-size:14px;
        font-style:italic;
        color:#fff;
        font-weight:700;
        text-transform:capitalize;
      ">
        ${booking.bookingStatus}
      </div>
</div>

  <!-- Content -->
  <div style="flex:1; display:flex; justify-content:space-between; gap:1px;">

    <!-- Left -->
    <div style="display:flex; flex-direction:column;">

    <div style="display:flex; gap:6px;">
    <div style="font-weight:bold; font-size:14px;">
      #${booking.shortBookingId} - ${booking.isAppbooking ? "App" : "Web"}
      </div>
      <div style="font-size:14px; font-weight:600;">
      ${booking.firstName} ${booking.lastName} - ${booking.phoneNumber}
      </div>
    </div>

      <div style="font-size:14px;">
        ${
          booking.booking
            ?.map(
              (serviceItem) => `
          <div style="display:flex;gap:6px;">
            <div style="display:flex; gap:6px; font-weight:600;">
              <div>${serviceItem.service.serviceOption}</div>
              <div>•</div>
              <div>${serviceItem.service.packageName}</div>
            </div>

            ${
              serviceItem.addonService?.length
                ? `<div style="font-size:12px;">
                    Addons: ${serviceItem.addonService
                      .map((a) => a.addonServiceName)
                      .join(", ")}
                  </div>`
                : `<div style="font-size:12px;">No Addons</div>`
            }
          </div>
        `,
            )
            .join("") ?? ""
        }
      </div>
    </div>

    <!-- Right -->
    <div style="display:flex; flex-direction:column; text-align:left; width:40%;">

      <div style="font-weight:bold; font-size:14px;">
        ${booking.locationType === "shop" ? "Shop" : "Mobile"}
      </div>


      <div style="font-size:15px; font-weight:800;">
        $${booking.totalAmount}
        <span style="font-size:14px; font-weight:600;">
          ${booking.coupon?.couponCode ? `(${booking.coupon.couponCode})` : ""}
        </span>
      </div>
    </div>
  </div>
</div>
`
}
